import { Controller } from "@hotwired/stimulus";
import { debounce } from "../utils/debounce";

// Connects to data-controller="autosubmit"
export default class extends Controller {
	static values = {
		delay: { type: Number, default: 1000 },
	};

	connect() {
		this.pending = false;
		this.debouncedSubmit = debounce(() => this.#run(), this.delayValue);
	}

	// An armed timer outlives the element it would submit: a turbo_stream that
	// replaces the cart drawer swaps the form out from under it, and
	// requestSubmit() on a detached form is refused by the browser, so the edit
	// would vanish with no request and no error. Dropping the timer here at least
	// makes that a clean no-op instead of a phantom submit.
	disconnect() {
		this.cancel();
	}

	// Debounced: the entry point for anything that fires in bursts — a keystroke,
	// a stepper arrow held down. Only the last one in a window reaches the server.
	submit() {
		this.pending = true;
		this.debouncedSubmit();
	}

	// Sends a queued submit NOW and drops its timer, so the two can never both
	// fire. Bind it to blur wherever the visit can end before the timer would
	// have: on the cart's quantity box, clicking "Finalizar compra" a keystroke after
	// typing must issue the PATCH before the visit starts.
	//
	// `pending` is the guard that keeps this from turning every blur into a
	// request. It is true exactly while a debounced submit is waiting — which is
	// why anything that submits by another route has to clear it (see cancel).
	flush() {
		if (!this.pending) return;

		this.cancel();
		this.#run();
	}

	// Forgets a queued submit. Bind it to `submit` on any form whose field is
	// debounced: a form with one field and no button submits on Enter, which
	// leaves the timer armed and fires a second, identical request a moment
	// later. Also the right hook for an outlet that calls immediateSubmit()
	// while a timer is running.
	cancel() {
		this.pending = false;
		this.debouncedSubmit.cancel();
	}

	immediateSubmit() {
		return this.element.requestSubmit();
	}

	#run() {
		this.pending = false;
		this.immediateSubmit();
	}
}
